import { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import phyberLogo from "@/assets/images/phyber-wordmark.png";
import { BACKEND_UNREACHABLE_EVENT, probeBackend } from "@/lib/backendStatus";

// While in maintenance, re-check the backend this often and reload the app
// once it's back so users don't have to refresh manually.
const RECHECK_INTERVAL_MS = 60000;

// Set REACT_APP_MAINTENANCE_MODE=true at build time to force the page on,
// regardless of backend health (e.g. for planned DB migrations).
const FORCED = process.env.REACT_APP_MAINTENANCE_MODE === "true";

// ── Inject styles once (mirrors the Login page look) ─────────────────────────
const MAINTENANCE_CSS = `
  .mnt-root {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background-color: #f8fafc;
    background-image: radial-gradient(circle, #cbd5e1 1px, transparent 1px);
    background-size: 24px 24px;
  }
  .mnt-heading {
    font-family: 'Lexend', sans-serif;
    font-weight: 600;
    letter-spacing: -0.03em;
  }
  .mnt-card {
    background: white;
    border: 1px solid rgb(226 232 240);
    border-radius: 20px;
    box-shadow: 0 8px 32px 0 rgb(79 70 229 / 0.08), 0 1px 3px 0 rgb(0 0 0 / 0.06);
    overflow: hidden;
  }
  .mnt-card-accent {
    height: 4px;
    background: linear-gradient(90deg, #6366f1 0%, #4f46e5 50%, #818cf8 100%);
  }
  /* Illustration: gear turns slowly, tools bob gently */
  @keyframes mntGear { to { transform: rotate(360deg); } }
  .mnt-gear { transform-box: fill-box; transform-origin: center; animation: mntGear 14s linear infinite; }
  @keyframes mntFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
  }
  .mnt-tools { animation: mntFloat 3.2s ease-in-out infinite; }
  @keyframes mntFadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mnt-fade-up   { animation: mntFadeUp 0.45s ease both; }
  .mnt-fade-up-d { animation: mntFadeUp 0.45s ease 0.06s both; }
  @media (prefers-reduced-motion: reduce) {
    .mnt-gear, .mnt-tools, .mnt-fade-up, .mnt-fade-up-d { animation: none; }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("maintenance-styles")) {
  const s = document.createElement("style");
  s.id = "maintenance-styles";
  s.textContent = MAINTENANCE_CSS;
  document.head.appendChild(s);
}

// Gear with a crossed spanner and screwdriver, drawn in the theme palette.
function MaintenanceIllustration() {
  return (
    <svg viewBox="0 0 200 160" className="w-48 h-auto" role="img" aria-label="Maintenance tools">
      <defs>
        <mask id="mnt-spanner-mask">
          <rect x="0" y="0" width="200" height="160" fill="white" />
          {/* open jaw */}
          <rect x="22" y="72" width="24" height="12" rx="2" fill="black" />
          {/* ring end hole */}
          <circle cx="156" cy="78" r="5" fill="black" />
        </mask>
      </defs>

      {/* ground shadow */}
      <ellipse cx="100" cy="148" rx="54" ry="5" fill="#e2e8f0" />

      {/* sparkles */}
      <circle cx="30" cy="34" r="3" fill="#c7d2fe" />
      <circle cx="172" cy="30" r="4" fill="#fed7aa" />
      <circle cx="178" cy="118" r="2.5" fill="#c7d2fe" />
      <circle cx="22" cy="112" r="2" fill="#fed7aa" />

      {/* gear */}
      <g className="mnt-gear">
        <circle cx="100" cy="78" r="50" fill="none" stroke="#c7d2fe" strokeWidth="12" strokeDasharray="13 13.18" />
        <circle cx="100" cy="78" r="45" fill="#e0e7ff" />
        <circle cx="100" cy="78" r="17" fill="#ffffff" />
      </g>

      <g className="mnt-tools">
        {/* spanner */}
        <g transform="rotate(-40 100 78)">
          <g mask="url(#mnt-spanner-mask)" fill="#4f46e5">
            <rect x="52" y="72" width="96" height="12" rx="6" />
            <circle cx="42" cy="78" r="17" />
            <circle cx="156" cy="78" r="12" />
          </g>
        </g>

        {/* screwdriver */}
        <g transform="rotate(40 100 78)">
          <path d="M44 78 L54 74.5 L54 81.5 Z" fill="#475569" />
          <rect x="53" y="75" width="66" height="6" rx="1.5" fill="#64748b" />
          <rect x="116" y="70" width="12" height="16" rx="3" fill="#ea580c" />
          <rect x="125" y="68" width="38" height="20" rx="9" fill="#f97316" />
          <rect x="134" y="72" width="2.5" height="12" rx="1.25" fill="#fdba74" />
          <rect x="142" y="72" width="2.5" height="12" rx="1.25" fill="#fdba74" />
          <rect x="150" y="72" width="2.5" height="12" rx="1.25" fill="#fdba74" />
        </g>
      </g>
    </svg>
  );
}

export function MaintenancePage({ autoRefresh = true }) {
  return (
    <div className="min-h-screen mnt-root flex items-center justify-center p-4" data-testid="maintenance-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 mnt-fade-up">
          <div className="flex justify-center mb-4">
            <img src={phyberLogo} alt="Phyber" className="h-11 w-auto max-w-[220px] object-contain drop-shadow-sm" />
          </div>
          <p className="text-sm text-slate-500 font-medium">Quiz &amp; Assessment Platform</p>
        </div>

        <div className="mnt-card mnt-fade-up-d">
          <div className="mnt-card-accent" />
          <div className="px-8 py-8 text-center">
            <div className="flex justify-center mb-4">
              <MaintenanceIllustration />
            </div>

            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Service temporarily unavailable
            </span>

            <h1 className="mnt-heading text-2xl text-slate-900 mb-2">Scheduled Maintenance</h1>
            <p className="text-[15px] text-slate-600 leading-relaxed">
              The system is currently under scheduled maintenance. Please contact your account
              support manager for more details.
            </p>

            {autoRefresh && (
              <p className="text-xs text-slate-400 mt-5 flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                This page refreshes automatically once the service is back.
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Secured by <span className="font-medium text-slate-500">Phyber</span>
        </p>
      </div>
    </div>
  );
}

// Probes the backend before mounting the app. Rendering the app (and with it
// AuthProvider/PortalAuthProvider) while the backend is down would fire their
// /auth/me and /student/me checks against a dead server.
export default function MaintenanceGate({ children }) {
  const [status, setStatus] = useState(FORCED ? "down" : "checking"); // checking | up | down
  const probingRef = useRef(false);
  const statusRef = useRef(status);
  statusRef.current = status;

  const check = useCallback(async () => {
    if (FORCED || probingRef.current) return;
    probingRef.current = true;
    const up = await probeBackend();
    probingRef.current = false;
    if (up && statusRef.current === "down") {
      // Backend came back — reload to remount the app from a clean state.
      window.location.reload();
      return;
    }
    setStatus(up ? "up" : "down");
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  // Mid-session failures: an API call couldn't reach the backend.
  useEffect(() => {
    const onUnreachable = () => {
      // A user who lost their own connection isn't seeing maintenance.
      if (navigator.onLine === false) return;
      check();
    };
    window.addEventListener(BACKEND_UNREACHABLE_EVENT, onUnreachable);
    return () => window.removeEventListener(BACKEND_UNREACHABLE_EVENT, onUnreachable);
  }, [check]);

  useEffect(() => {
    if (status !== "down" || FORCED) return;
    const id = setInterval(check, RECHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status, check]);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (status === "down") {
    return <MaintenancePage autoRefresh={!FORCED} />;
  }
  return children;
}
